import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button, message, Modal, ModalProps, Typography, Upload, UploadFile } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import { t } from '../../i18n';
import { ImportFileCollection } from '../../types';
import Form from '../Form';

const mimeTypes = {
  csv: 'text/csv',
};

function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

const supportedFiles = ['.csv', 'text/csv'];

const maxFileSizeInBytes = 10_485_760; //10Mb

const TITLES: Record<ImportFileCollection['type'], string> = {
  IMPORT_CANDIDATES: t('Importar do Banco de Talentos') as string,
  MATCH_RESULTS: t('Importar Match dos Candidatos') as string,
  TEST_RESULTS: t('Importar Notas dos Candidatos') as string,
};

interface ImportModalFormValues {
  attachment: UploadFile[];
}

interface ImportModalProps extends ModalProps {
  type: ImportFileCollection['type'];
  onSuccess?: (
    data: void,
    variables: ImportModalFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const ImportFileModal = ({ type, onSuccess, okButtonProps, ...props }: ImportModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const mutation = useAPIMutation(
    async ({ attachment }: ImportModalFormValues, api) => {
      const formData = new FormData();
      formData.append('file', attachment[0].originFileObj ?? '');
      formData.append('type', type);
      await api.post('/import-files/', formData, {
        maxBodyLength: maxFileSizeInBytes,
      });
    },
    {
      async onSettled() {
        await queryClient.invalidateQueries('/import-files/');
      },
      onSuccess,
    }
  );

  const hasPendingImportFile = useAPIQuery(
    ['/import-files/', { params: { status__in: 'PENDING,IN_PROGRESS', type } }],
    {
      select(data) {
        return data?.count > 0;
      },
    }
  );

  return (
    <Modal
      onOk={() => form.submit()}
      okButtonProps={{
        loading: mutation.isLoading,
        disabled: hasPendingImportFile.data,
        ...okButtonProps,
      }}
      title={<Typography.Text>{TITLES[type]}</Typography.Text>}
      {...props}
    >
      <Helmet>
        <title>
          {TITLES[type]} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<ImportModalFormValues>
        form={form}
        layout="vertical"
        initialValues={{}}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <Form.Item
          label={t('Arquivo')}
          name="attachment"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            maxCount={1}
            listType="text"
            accept={supportedFiles.join(',')}
            beforeUpload={(file: UploadFile) => {
              if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                if (!file.size) {
                  message.error(t('Erro com o arquivo.'));
                  return Upload.LIST_IGNORE;
                }
                if (file.size >= maxFileSizeInBytes) {
                  message.error(t('Por favor, selecione um arquivo de até 10Mb no campo Arquivo.'));
                  return Upload.LIST_IGNORE;
                }

                return false;
              } else {
                message.error(t('Por favor, selecione um arquivo no formato ".csv".'));
                return Upload.LIST_IGNORE;
              }
            }}
            disabled={hasPendingImportFile.data}
          >
            <Button icon={<UploadOutlined />}>
              {' '}
              <Trans>Subir arquivo</Trans>
            </Button>
          </Upload>
        </Form.Item>
        {hasPendingImportFile.data && (
          <Alert
            message={
              <Typography.Text>
                <Typography.Text strong>
                  <Trans>Atenção:</Trans>{' '}
                </Typography.Text>
                <Trans>
                  Há um arquivo em processo de importação. Aguarde até que o processo seja
                  finalizado para importar um novo.
                </Trans>
              </Typography.Text>
            }
            type="warning"
            showIcon
          />
        )}
      </Form>
    </Modal>
  );
};

export default ImportFileModal;
